import {
  KIOSK_AVAILABILITY_RECHECK_INTERVAL,
  weekdayStringToDayjsNumberMap,
} from '@/constants';
import { useInterval } from '@/hooks/useInterval';
import { useKioskStore } from '@/stores/kiosk';
import { useListStore } from '@/stores/list';
import { useUsersStore } from '@/stores/users';
import { EKioskRouterNames } from '@/types/navigation';
import { getNowInTimezone, getWallTimeInTimezone } from '@/utils/convertDate';
import { isPropertyOf } from '@/utils/isPropertyOf';
import { useCallback, useEffect } from 'react';
import { matchPath, useNavigate } from 'react-router-dom';

const unavailableKioskPath = `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__UNAVAILABLE}`;
const unavailableKioskNoticePath = `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__UNAVAILABLE_NOTICE}`;

export const useKioskAvailability = () => {
  const kioskData = useKioskStore((state) => state.kioskData);
  const timezones = useListStore((state) => state.timezones);
  const navigate = useNavigate();
  const { roleId, logout } = useUsersStore((state) => ({
    roleId: state.roleId,
    logout: state.logout,
  }));

  const navigateToUnavailableKioskPage = useCallback(async () => {
    const isAlreadyNavigated = [
      unavailableKioskPath,
      unavailableKioskNoticePath,
    ].some((url) => !!matchPath(url, location.pathname));
    const handler = (withNotice: boolean) => {
      if (isAlreadyNavigated) return;
      navigate(withNotice ? unavailableKioskNoticePath : unavailableKioskPath);
    };
    try {
      handler(!!roleId);
      if (roleId) {
        await logout();
      }
    } catch (error) {
      console.info('Error during logout:', error);
      handler(false);
    }
  }, [logout, navigate, roleId]);

  const checkKioskAvailability = useCallback(async () => {
    if (!kioskData) return;
    const todayDate = getNowInTimezone(kioskData.timezone, timezones);
    if (!todayDate) return;
    const todayWeekday = todayDate.get('day'); // 0 - Sunday, 6 - Saturday
    const weekdaysAvailability = kioskData?.schedule
      ?.map(({ day, startTime, endTime }) => ({
        day: isPropertyOf(day, weekdayStringToDayjsNumberMap)
          ? weekdayStringToDayjsNumberMap[day]
          : null,
        startTime,
        endTime,
      }))
      .filter(({ day }) => day !== null);
    // need to use filter here because there could be mutile time periods in one day
    const weekdayMatch = weekdaysAvailability?.filter(
      ({ day }) => day === todayWeekday,
    );
    if (!weekdayMatch?.length) {
      // No availability found for today
      // this needs to be async-await because syncronious navigation breaks
      // you are navigated before useUsersStore cleared and then you navigated back
      // to avoid this loop - complete logout with store clear before navigating
      await navigateToUnavailableKioskPage();
      return;
    }
    // any timer period for today should satisfy condition
    const isTimeBetween = weekdayMatch.some((day) => {
      return todayDate.isBetween(
        getWallTimeInTimezone(
          todayDate,
          day.startTime,
          kioskData.timezone,
          timezones,
        ),
        getWallTimeInTimezone(
          todayDate,
          day.endTime,
          kioskData.timezone,
          timezones,
        ),
        'second',
      );
    });
    if (!isTimeBetween) {
      // No time match found for today
      await navigateToUnavailableKioskPage();
      return;
    }
    if (matchPath(unavailableKioskPath, location.pathname)) {
      // Go back to kiosk landing if available
      navigate(EKioskRouterNames.KIOSK);
    }
  }, [kioskData, timezones, navigate, navigateToUnavailableKioskPage]);

  useEffect(() => {
    void checkKioskAvailability();
  }, [checkKioskAvailability]);

  useInterval(() => {
    void checkKioskAvailability();
  }, KIOSK_AVAILABILITY_RECHECK_INTERVAL);
};
